import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import { Toast } from "./Toast";
import { useUser } from "../context/UserContext";
import { isFirebaseConfigured } from "../auth/firebaseClient";
import {
  DISPLAY_NAME_PROFANITY_MESSAGE,
  displayNameContainsProfanity,
} from "../utils/displayNamePolicy";

type Props = {
  open: boolean;
  onClose: () => void;
  gameLabel?: string;
};

function isStrongPassword(pw: string): boolean {
  if (pw.length < 8) return false;
  return /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw) && /[^A-Za-z0-9]/.test(pw);
}

function mapAuthError(e: unknown, mode: "login" | "signup"): string {
  const raw = e instanceof Error ? e.message : "Authentication failed";
  if (raw.includes("auth/invalid-email")) return "Please enter a valid email address.";
  if (raw.includes("auth/missing-password")) return "Please enter your password.";
  if (raw.includes("auth/invalid-credential")) return "Email or password is incorrect.";
  if (raw.includes("auth/user-not-found")) return "No account found for this email.";
  if (raw.includes("auth/wrong-password")) return "Email or password is incorrect.";
  if (raw.includes("auth/email-already-in-use")) return "An account with this email already exists. Try Login.";
  if (raw.includes("auth/weak-password")) return "Password is too weak. Use 8+ chars with mixed complexity.";
  if (raw.includes("auth/popup-closed-by-user")) return "Sign-in popup was closed. Please try again.";
  if (raw.includes("auth/network-request-failed")) return "Network issue. Check connection and try again.";
  if (raw.includes("not allowed")) return DISPLAY_NAME_PROFANITY_MESSAGE;
  return mode === "signup"
    ? "Could not create account. Please check details and try again."
    : "Could not log in. Please check your credentials and try again.";
}

function mapPasswordResetError(e: unknown): string {
  const raw = e instanceof Error ? e.message : "Something went wrong";
  if (raw.includes("auth/invalid-email")) return "Please enter a valid email address.";
  if (raw.includes("auth/missing-email")) return "Please enter your email address.";
  if (raw.includes("auth/too-many-requests")) return "Too many attempts. Wait a few minutes and try again.";
  if (raw.includes("auth/network-request-failed")) return "Network issue. Check connection and try again.";
  return "Could not send reset email. Try again later.";
}

export const PostGameGuestSignInDialog = ({ open, onClose, gameLabel }: Props) => {
  const { signInWithGoogle, signInWithEmailPassword, signUpWithEmailPassword, sendPasswordResetEmail } = useUser();
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signupDisplayName, setSignupDisplayName] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetDialogError, setResetDialogError] = useState<string | null>(null);

  const normalizedEmail = email.trim();
  const loginFieldsValid = normalizedEmail.length > 0 && password.length > 0;
  const signupNickProfane =
    signupDisplayName.trim().length > 0 && displayNameContainsProfanity(signupDisplayName);
  const signupFieldsValid =
    signupDisplayName.trim().length > 0 &&
    !signupNickProfane &&
    normalizedEmail.length > 0 &&
    password.length > 0 &&
    confirmPassword.length > 0 &&
    isStrongPassword(password) &&
    password === confirmPassword;

  useEffect(() => {
    if (!open) return;
    setAuthMode("login");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setSignupDisplayName("");
    setAuthError(null);
    setForgotOpen(false);
    setResetEmail("");
    setResetSending(false);
    setResetSent(false);
    setResetDialogError(null);
  }, [open]);

  const subtitle =
    gameLabel != null
      ? `You played ${gameLabel} as a guest. Sign in to add your stats to the leaderboard.`
      : "You played as a guest. Sign in to add your stats to the leaderboard.";

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Save this session to your profile?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            {subtitle}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Literature and Rummy games you finished are applied as soon as you sign in. Addata ranked totals
            apply when the full set completes and you were seated for those deals.
          </Typography>

          {isFirebaseConfigured ? (
            <Stack spacing={1.5}>
              <ToggleButtonGroup
                color="primary"
                value={authMode}
                exclusive
                fullWidth
                onChange={(_e, next) => {
                  if (next) setAuthMode(next);
                }}
                sx={{ "& .MuiToggleButton-root": { py: 0.65 } }}
              >
                <ToggleButton value="login">Login</ToggleButton>
                <ToggleButton value="signup">Sign up</ToggleButton>
              </ToggleButtonGroup>
              {authMode === "signup" ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Nickname"
                  value={signupDisplayName}
                  onChange={(e) => setSignupDisplayName(e.target.value)}
                  error={signupNickProfane}
                  helperText={signupNickProfane ? DISPLAY_NAME_PROFANITY_MESSAGE : "Shown on leaderboards."}
                />
              ) : null}
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Password"
                type="password"
                autoComplete={authMode === "login" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText={
                  authMode === "signup" ? "8+ chars with upper, lower, number, and symbol." : undefined
                }
              />
              {authMode === "login" ? (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => {
                      setResetEmail(normalizedEmail);
                      setResetSent(false);
                      setResetDialogError(null);
                      setForgotOpen(true);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              ) : null}
              {authMode === "signup" ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Confirm password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              ) : null}
              <Button
                fullWidth
                variant="contained"
                disabled={authMode === "login" ? !loginFieldsValid : !signupFieldsValid}
                onClick={async () => {
                  setAuthError(null);
                  try {
                    if (authMode === "login") {
                      if (!loginFieldsValid) throw new Error("Please enter both email and password.");
                      await signInWithEmailPassword(email, password);
                      return;
                    }
                    if (!signupDisplayName.trim()) throw new Error("Nickname is required for sign up");
                    if (!isStrongPassword(password)) {
                      throw new Error(
                        "Password must be 8+ chars and include uppercase, lowercase, number, and symbol."
                      );
                    }
                    if (password !== confirmPassword) throw new Error("Passwords do not match");
                    if (displayNameContainsProfanity(signupDisplayName.trim())) {
                      throw new Error(DISPLAY_NAME_PROFANITY_MESSAGE);
                    }
                    await signUpWithEmailPassword(email, password, signupDisplayName.trim());
                  } catch (e) {
                    setAuthError(mapAuthError(e, authMode));
                  }
                }}
              >
                {authMode === "login" ? "Log in with email" : "Create account"}
              </Button>
              <Divider sx={{ my: 0.5 }}>or</Divider>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={async () => {
                  setAuthError(null);
                  try {
                    await signInWithGoogle();
                  } catch (e) {
                    setAuthError(mapAuthError(e, "login"));
                  }
                }}
              >
                Sign in with Google
              </Button>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Email and Google sign-in are not available in this build (Firebase not configured).
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={onClose} color="inherit">
            Not now
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={forgotOpen}
        onClose={() => {
          if (!resetSending) setForgotOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="postgame-forgot-password-title"
      >
        <DialogTitle id="postgame-forgot-password-title">Reset password</DialogTitle>
        <DialogContent>
          {resetSent ? (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
              If an account exists for that email, we sent a link to reset your password. Check your inbox and spam
              folder, then return here to sign in.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pt: 0.5 }}>
                Enter the email you used for your account. We will send a reset link.
              </Typography>
              <TextField
                fullWidth
                autoFocus
                label="Email"
                type="email"
                value={resetEmail}
                onChange={(e) => {
                  setResetEmail(e.target.value);
                  setResetDialogError(null);
                }}
                error={Boolean(resetDialogError)}
                helperText={resetDialogError || undefined}
                margin="dense"
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {resetSent ? (
            <Button onClick={() => setForgotOpen(false)} variant="contained" fullWidth>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={() => setForgotOpen(false)} disabled={resetSending}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={resetSending || !resetEmail.trim()}
                onClick={async () => {
                  setResetDialogError(null);
                  setResetSending(true);
                  try {
                    await sendPasswordResetEmail(resetEmail);
                    setResetSent(true);
                  } catch (e: unknown) {
                    const code =
                      typeof e === "object" && e !== null && "code" in e
                        ? String((e as { code: string }).code)
                        : "";
                    if (code === "auth/user-not-found") {
                      setResetSent(true);
                    } else {
                      setResetDialogError(mapPasswordResetError(e));
                    }
                  } finally {
                    setResetSending(false);
                  }
                }}
              >
                {resetSending ? "Sending…" : "Send reset link"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {authError ? <Toast severity="error" message={authError} onClose={() => setAuthError(null)} /> : null}
    </>
  );
};
